/**
 * Hook wrapper around the contract drawer reactive variable.
 *
 * Exposes the current drawer state plus imperative open/close/setMode
 * helpers so containers can manipulate the drawer without touching the
 * raw `makeVar`. The state object is read-only — callers update via the
 * helper methods, which keeps the reducer-style update logic local and
 * testable.
 * @module features/contracts/hooks/useContractDrawerState
 */

import { useReactiveVar } from "@apollo/client";
import { useCallback, useMemo } from "react";

import {
  CONTRACT_DRAWER_DEFAULT_DOCUMENT_TYPE,
  CONTRACT_DRAWER_DEFAULT_MODE,
} from "../constants";
import {
  contractDrawerVar,
  resetContractDrawer,
} from "../stores/contractDrawerStore";
import type {
  ContractDocumentType,
  ContractDrawerMode,
  ContractDrawerState,
  ContractsDocument,
  ContractTierName,
} from "../types";

/** Arguments accepted by `useContractDrawerState().openForPlayer`. */
interface OpenForPlayerArgs {
  readonly playerId: string;
  readonly playerName: string;
  readonly documentType?: ContractDocumentType;
  /**
   * When true, opens the drawer in archived (read-only) mode. Defaults to
   * false for backward compatibility with the Active-view call site.
   */
  readonly archived?: boolean;
  /**
   * SE-4333 item D — tier label from the row the drawer was opened
   * from. Seeds the reduced-form fallback metadata row while the
   * identity query resolves. Optional because the deep-link path in
   * `SquadScreenContainer` has no row in hand.
   */
  readonly tier?: ContractTierName | null;
  /**
   * SE-4333 item D — player age from the row the drawer was opened
   * from. Optional for the same reason as `tier`.
   */
  readonly age?: number | null;
}

/** Arguments accepted by `useContractDrawerState().prepareUpload`. */
interface PrepareUploadArgs {
  readonly playerId: string;
  readonly playerName: string;
  readonly documentType: ContractDocumentType;
}

/** Public API returned by `useContractDrawerState`. */
export interface UseContractDrawerStateResult {
  readonly state: ContractDrawerState;
  readonly openForPlayer: (args: OpenForPlayerArgs) => void;
  readonly close: () => void;
  readonly setMode: (mode: ContractDrawerMode) => void;
  readonly setDocumentType: (documentType: ContractDocumentType) => void;
  /**
   * Selects a document for preview, atomically flipping the drawer into
   * `preview` mode. Passing `null` clears the selection and returns the
   * drawer to `list` mode.
   */
  readonly selectDocument: (document: ContractsDocument | null) => void;
  /**
   * SE-4785 — atomically seeds the drawer reactive var with a player +
   * document type and flips `mode` to `"upload"` WITHOUT setting
   * `open: true`. Used by the mobile full-page contract route at
   * `/squad/contracts/[playerId]`, where the desktop drawer is never
   * mounted: we need the shared `ContractUploadSectionContainer` (which
   * reads `playerId` / `playerName` / `documentType` from the same
   * reactive var) to have valid context, but flipping `open: true`
   * would re-fire the desktop drawer's mobile-redirect effect and push
   * the route again. Keeping `open: false` lets the mobile screen drive
   * the upload UI off `state.mode` while the desktop drawer stays
   * dormant.
   */
  readonly prepareUpload: (args: PrepareUploadArgs) => void;
}

/**
 * Subscribes to the contract drawer reactive variable and exposes
 * imperative helpers.
 * @returns The current drawer state plus open/close/setMode helpers.
 */
export function useContractDrawerState(): UseContractDrawerStateResult {
  const state = useReactiveVar(contractDrawerVar);

  const openForPlayer = useCallback(
    ({
      playerId,
      playerName,
      documentType = CONTRACT_DRAWER_DEFAULT_DOCUMENT_TYPE,
      archived = false,
      tier = null,
      age = null,
    }: OpenForPlayerArgs) => {
      contractDrawerVar({
        open: true,
        playerId,
        playerName,
        documentType,
        mode: CONTRACT_DRAWER_DEFAULT_MODE,
        selectedDocument: null,
        archived,
        tier,
        age,
      });
    },
    []
  );

  const close = useCallback(() => {
    resetContractDrawer();
  }, []);

  const setMode = useCallback((mode: ContractDrawerMode) => {
    contractDrawerVar({ ...contractDrawerVar(), mode });
  }, []);

  const setDocumentType = useCallback((documentType: ContractDocumentType) => {
    contractDrawerVar({ ...contractDrawerVar(), documentType });
  }, []);

  const selectDocument = useCallback((document: ContractsDocument | null) => {
    contractDrawerVar({
      ...contractDrawerVar(),
      selectedDocument: document,
      mode: document ? "preview" : "list",
    });
  }, []);

  const prepareUpload = useCallback(
    ({ playerId, playerName, documentType }: PrepareUploadArgs) => {
      // Read the current value so any in-flight fields (e.g. tier/age
      // already seeded by an opening row click on desktop) survive the
      // update. We deliberately keep `open: false` — see the JSDoc on
      // `prepareUpload` for the redirect-loop rationale.
      const current = contractDrawerVar();
      contractDrawerVar({
        ...current,
        open: false,
        playerId,
        playerName,
        documentType,
        mode: "upload",
        selectedDocument: null,
      });
    },
    []
  );

  return useMemo(
    () => ({
      state,
      openForPlayer,
      close,
      setMode,
      setDocumentType,
      selectDocument,
      prepareUpload,
    }),
    [
      state,
      openForPlayer,
      close,
      setMode,
      setDocumentType,
      selectDocument,
      prepareUpload,
    ]
  );
}
